import React, { useState, useEffect, useRef } from 'react';
import { getVfProjectsData } from '../services/api';
import './VfProjectsDropdown.css';
import { matchesSearch } from '../utils/search';

const VfProjectsDropdown = ({ value, onChange, placeholder = "Select VF project..." }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch VF projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await getVfProjectsData();
        const projectsData = response.data || {};
        
        // Convert object to array of {key, value} pairs
        const projectsArray = Object.entries(projectsData).map(([key, value]) => ({
          key,
          value
        }));
        
        setProjects(projectsArray);
        setFilteredProjects(projectsArray);
      } catch (error) {
        console.error('Error fetching VF projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => 
        matchesSearch(searchTerm, project.key, project.value)
      );
      setFilteredProjects(filtered);
    }
  }, [searchTerm, projects]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (project, e) => {
    // preventDefault: options sit inside <label>s in AgentEditor; without this the
    // label re-focuses the input and onClick reopens the menu immediately.
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onChange(project.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const getDisplayValue = () => {
    if (!value) return '';
    const selectedProject = projects.find(project => project.value === value);
    return selectedProject ? `${selectedProject.key} (${selectedProject.value})` : value;
  };

  return (
    <div className="vf-projects-dropdown" ref={dropdownRef}>
      <input
        type="text"
        value={isOpen ? searchTerm : getDisplayValue()}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="vf-projects-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && (
        <div className="vf-projects-dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading VF projects...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="dropdown-no-results">No VF projects found</div>
          ) : (
            filteredProjects.map((project) => (
              <div
                key={project.key}
                className="dropdown-option"
                onMouseDown={(e) => handleSelect(project, e)}
              >
                <div className="project-key">{project.key}</div>
                <div className="project-value">{project.value}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default VfProjectsDropdown;
